import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Box, Column, ConfirmationModal, Divider, FileDropzone, IconButton, Row, Typography, useDevice } from 'seagull';
import { Checkmark, ColorThumbDown, ColorThumbUp, Document, ExclamationMark, Import, ThumbDown, ThumbUp } from 'seagull/icons';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { CircularProgress } from './CircularProgress';
import { isKnownErrorReason, useImportAgreement } from './hooks/useImportAgreement';
import s from './ImportAgreementModal.module.scss';
import { GetImportAgreementModalTID, STEP_INTERVAL_MS, SUCCESS_DELAY_MS } from './ImportAgreementModal.utils';
const ANALYZING_STEPS = [
    0,
    1,
    2
];
const progressPercent = (completedStep)=>{
    if (completedStep >= 2) return 100;
    if (completedStep === 1) return 66;
    if (completedStep === 0) return 33;
    return 16;
};
const ImportModalShell = (_0)=>{
    let { testIds, dismissModal, contentClassName, bodyClassName } = _0, rest = _object_without_properties(_0, [
        "testIds",
        "dismissModal",
        "contentClassName",
        "bodyClassName"
    ]);
    return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread({
        width: "550px",
        skin: "transparent",
        headerClassName: s.shellHeader,
        testId: testIds.Root(),
        onCloseButtonClick: dismissModal,
        contentClassName: cn(s.shellContent, contentClassName),
        bodyClassName: cn(s.shellBody, bodyClassName),
        style: {
            minHeight: '374px'
        }
    }, rest));
};
export const ImportAgreementModal = (param)=>{
    let { dismissModal, onSuccess, testId, stepIntervalMs = STEP_INTERVAL_MS, successDelayMs = SUCCESS_DELAY_MS } = param;
    const { ImportAgreementModalTestIds } = useMemo(()=>GetImportAgreementModalTID(testId), [
        testId
    ]);
    const { t } = useLocaleKeys();
    const { isMobileDevice } = useDevice();
    const { AGREEMENT_IMPORT_TRANSCRIPT } = useFeatureFlags();
    const { modalState, isUploading, fileName, errorReason, conversationId, extractionSuccess, processFile, submitFeedback, resetToUpload, notifySuccess } = useImportAgreement(ImportAgreementModalTestIds, onSuccess, !!AGREEMENT_IMPORT_TRANSCRIPT);
    if (modalState === 'upload') {
        return /*#__PURE__*/ _jsx(ImportModalShell, {
            bi: {
                primaryButton: {
                    name: 'import_agreement_upload'
                },
                secondaryButton: {
                    name: 'import_agreement_upload_cancel'
                }
            },
            testIds: ImportAgreementModalTestIds,
            dismissModal: dismissModal,
            showFooter: false,
            title: "",
            bodyClassName: s.uploadModalBody,
            content: /*#__PURE__*/ _jsxs(Column, {
                className: s.uploadColumn,
                children: [
                    /*#__PURE__*/ _jsxs(Column, {
                        className: s.uploadHeader,
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: isMobileDevice ? 'paragraph1' : 'h5',
                                weight: "bold",
                                testId: ImportAgreementModalTestIds.UploadTitle(),
                                children: t.agreementImport.uploadModal.title()
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                skin: "secondary",
                                testId: ImportAgreementModalTestIds.UploadSubtitle(),
                                children: t.agreementImport.uploadModal.subtitle()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(FileDropzone, {
                        bi: {
                            name: 'import_agreement_file'
                        },
                        accept: AGREEMENT_IMPORT_TRANSCRIPT ? '.pdf,.txt' : '.pdf',
                        testId: ImportAgreementModalTestIds.Dropzone(),
                        inputTestId: "".concat(ImportAgreementModalTestIds.Dropzone(), "-input"),
                        onFileSelected: processFile,
                        className: s.uploadDropzone,
                        renderContent: ()=>/*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: s.uploadIconCircle,
                                        children: /*#__PURE__*/ _jsx(Import, {
                                            size: 24,
                                            className: s.uploadIcon
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(Column, {
                                        className: s.dropzoneTextBlock,
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph2",
                                                weight: "normal",
                                                skin: "primary",
                                                children: t.agreementImport.uploadModal.dropzoneText()
                                            }),
                                            /*#__PURE__*/ _jsx(Typography, {
                                                skin: "secondary",
                                                variant: "paragraph3",
                                                children: AGREEMENT_IMPORT_TRANSCRIPT ? t.agreementImport.uploadModal.dropzoneHintWithTranscript() : t.agreementImport.uploadModal.dropzoneHint()
                                            })
                                        ]
                                    })
                                ]
                            })
                    })
                ]
            })
        });
    }
    if (modalState === 'analyzing') {
        return /*#__PURE__*/ _jsx(AnalyzingContent, {
            testIds: ImportAgreementModalTestIds,
            fileName: fileName,
            extractionSuccess: extractionSuccess,
            isUploading: isUploading,
            dismissModal: dismissModal,
            notifySuccess: notifySuccess,
            stepIntervalMs: stepIntervalMs,
            successDelayMs: successDelayMs
        });
    }
    if (modalState === 'error' && isKnownErrorReason(errorReason)) {
        return /*#__PURE__*/ _jsx(ErrorContent, {
            testIds: ImportAgreementModalTestIds,
            errorReason: errorReason,
            conversationId: conversationId,
            submitFeedback: submitFeedback,
            resetToUpload: resetToUpload,
            dismissModal: dismissModal
        });
    }
    return null;
};
const AnalyzingContent = (param)=>{
    let { testIds, fileName, extractionSuccess, isUploading, dismissModal, notifySuccess, stepIntervalMs, successDelayMs } = param;
    const { t } = useLocaleKeys();
    const { isMobileDevice } = useDevice();
    const [completedStep, setCompletedStep] = useState(-1);
    const successCalledRef = useRef(false);
    const stepLabels = [
        t.agreementImport.analyzingModal.step1(),
        t.agreementImport.analyzingModal.step2(),
        t.agreementImport.analyzingModal.step3()
    ];
    useEffect(()=>{
        if (extractionSuccess) {
            setCompletedStep(2);
            return;
        }
        if (isUploading) return;
        const interval = setInterval(()=>{
            setCompletedStep((prev)=>{
                if (prev >= 2) {
                    clearInterval(interval);
                    return prev;
                }
                return prev + 1;
            });
        }, stepIntervalMs);
        return ()=>clearInterval(interval);
    }, [
        extractionSuccess,
        isUploading
    ]);
    useEffect(()=>{
        if (extractionSuccess && completedStep >= 2 && !successCalledRef.current) {
            const timeout = setTimeout(()=>{
                successCalledRef.current = true;
                notifySuccess();
            }, successDelayMs);
            return ()=>clearTimeout(timeout);
        }
    }, [
        extractionSuccess,
        completedStep,
        notifySuccess
    ]);
    return /*#__PURE__*/ _jsx(ImportModalShell, {
        bi: {
            primaryButton: {
                name: 'import_agreement_analyzing'
            },
            secondaryButton: {
                name: 'import_agreement_analyzing_cancel'
            }
        },
        testIds: testIds,
        dismissModal: dismissModal,
        showFooter: false,
        title: "",
        cancelButtonDisabled: true,
        content: /*#__PURE__*/ _jsxs(Column, {
            className: s.analyzingContent,
            children: [
                /*#__PURE__*/ _jsx(CircularProgress, {
                    percent: progressPercent(completedStep)
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.AnalyzingTitle(),
                    variant: "h5",
                    weight: "normal",
                    className: s.analyzingTitle,
                    children: t.agreementImport.analyzingModal.title()
                }),
                /*#__PURE__*/ _jsxs(Box, {
                    testId: testIds.FileNameChip(),
                    className: s.fileNameChip,
                    children: [
                        /*#__PURE__*/ _jsx(Document, {
                            size: 16
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph3",
                            skin: "secondary",
                            children: fileName
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Divider, {
                    className: s.divider
                }),
                /*#__PURE__*/ _jsx(Column, {
                    className: cn(s.stepsContainer, {
                        [s.stepsContainerMobile]: isMobileDevice
                    }),
                    children: ANALYZING_STEPS.map((stepIndex)=>{
                        const isComplete = stepIndex <= completedStep;
                        const isActive = stepIndex === completedStep + 1;
                        return /*#__PURE__*/ _jsxs(Box, {
                            testId: testIds.Step(String(stepIndex)),
                            className: s.step,
                            children: [
                                isComplete ? /*#__PURE__*/ _jsx(Box, {
                                    testId: testIds.StepCheckmark(String(stepIndex)),
                                    className: s.stepCheckmark,
                                    children: /*#__PURE__*/ _jsx(Checkmark, {
                                        size: 16
                                    })
                                }) : /*#__PURE__*/ _jsx(Box, {
                                    className: cn(s.stepDot, {
                                        [s.stepDotActive]: isActive
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph1",
                                    weight: "light",
                                    skin: isActive ? 'magical' : isComplete ? 'secondary' : 'disabled',
                                    children: stepLabels[stepIndex]
                                })
                            ]
                        }, stepIndex);
                    })
                })
            ]
        })
    });
};
const ErrorContent = (param)=>{
    let { testIds, errorReason, conversationId, submitFeedback, resetToUpload, dismissModal } = param;
    const { t } = useLocaleKeys();
    const [selectedRating, setSelectedRating] = useState(null);
    const { title, description } = getErrorText(t.agreementImport.errorModal, errorReason);
    const submitSelectedFeedback = ()=>{
        if (selectedRating != null) submitFeedback(selectedRating);
    };
    const handleDismiss = ()=>{
        submitSelectedFeedback();
        dismissModal();
    };
    const handleRetry = ()=>{
        submitSelectedFeedback();
        resetToUpload();
    };
    return /*#__PURE__*/ _jsx(ImportModalShell, {
        bi: {
            primaryButton: {
                name: 'import_agreement_retry'
            },
            secondaryButton: {
                name: 'import_agreement_cancel'
            }
        },
        testIds: testIds,
        dismissModal: handleDismiss,
        title: "",
        secondaryButtonLabel: t.agreementImport.errorModal.cancelButton(),
        secondaryButtonTestId: testIds.CancelButton(),
        onSecondaryButtonClick: handleDismiss,
        primaryButtonLabel: t.agreementImport.errorModal.retryButton(),
        primaryButtonTestId: testIds.RetryButton(),
        onPrimaryButtonClick: handleRetry,
        content: /*#__PURE__*/ _jsxs(Column, {
            className: s.errorContent,
            children: [
                /*#__PURE__*/ _jsx(Box, {
                    className: s.errorIconContainer,
                    children: /*#__PURE__*/ _jsx(ExclamationMark, {})
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.ErrorTitle(),
                    variant: "h6",
                    weight: "normal",
                    className: s.errorTitle,
                    children: title
                }),
                /*#__PURE__*/ _jsx(Typography, {
                    testId: testIds.ErrorDescription(),
                    variant: "paragraph2",
                    skin: "secondary",
                    className: s.errorDescription,
                    children: description
                }),
                /*#__PURE__*/ _jsx(Divider, {
                    className: s.divider
                }),
                conversationId != null && /*#__PURE__*/ _jsxs(Box, {
                    testId: testIds.FeedbackRow(),
                    className: s.feedbackRow,
                    children: [
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            skin: "secondary",
                            children: t.agreementImport.errorModal.feedbackPrompt()
                        }),
                        /*#__PURE__*/ _jsxs(Row, {
                            className: s.thumbButtons,
                            gap: 4,
                            children: [
                                /*#__PURE__*/ _jsx(IconButton, {
                                    bi: {
                                        name: 'import_agreement_feedback_thumbs_up'
                                    },
                                    testId: testIds.ThumbsUpButton(),
                                    skin: "transparent",
                                    onClick: ()=>setSelectedRating('thumbsUp'),
                                    children: selectedRating === 'thumbsUp' ? /*#__PURE__*/ _jsx(ColorThumbUp, {}) : /*#__PURE__*/ _jsx(ThumbUp, {})
                                }),
                                /*#__PURE__*/ _jsx(IconButton, {
                                    bi: {
                                        name: 'import_agreement_feedback_thumbs_down'
                                    },
                                    testId: testIds.ThumbsDownButton(),
                                    skin: "transparent",
                                    onClick: ()=>setSelectedRating('thumbsDown'),
                                    children: selectedRating === 'thumbsDown' ? /*#__PURE__*/ _jsx(ColorThumbDown, {}) : /*#__PURE__*/ _jsx(ThumbDown, {})
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
const getErrorText = (errorModal, errorReason)=>{
    switch(errorReason){
        case 'insufficient_data':
        case 'not_an_agreement':
            return {
                title: errorModal.insufficientData.title(),
                description: errorModal.insufficientData.description()
            };
    }
};
